
































































import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import Yaml from 'yaml';
import { LoginInfo } from '../../types/type';
import { _Boolean } from '@/API';
import { JobItem } from '@/appsync/graphql/job';
import { TaskItem } from '@/appsync/graphql/task';
import { i18n } from '@/plugins/i18n/i18n';
import { getSHA3of } from '@/utils/CommonUtils';

type JobsWithSignature = {
  items: JobItem[];
  signature: string;
};

@Component
export default class ExportJobFileDialog extends Vue {
  private exportTarget: 'all' | 'select' = 'all';
  private exportFormat: 'csv' | 'yaml' = 'csv';
  private checkedJobs: JobItem[] = [];
  private searchJobWord: string = '';

  private readonly jobTableHeaders = [
    {
      text: i18n.tc('jobScheduler.jobs.jobCode'),
      align: 'start',
      value: 'jobCode',
      width: 130,
    },
    {
      text: i18n.tc('jobScheduler.jobs.jobTitle'),
      align: 'start',
      value: 'title',
      width: 130,
    },
  ];

  @Prop()
  private showExportJobFileDialog!: boolean;
  @Prop()
  private jobs!: JobItem[];
  @Prop()
  private loginInfo!: LoginInfo;
  @Emit()
  onCloseDialog() {}

  private jobTableRows(jobs: JobItem[]) {
    return jobs.map(job => ({
      ...job,
      createdAt: dayjs(job.createdAt).format('YYYY/MM/DD HH:mm:ss'),
    }));
  }

  private convertTasks(tasks: { items: TaskItem[] }) {
    return {
      items: (tasks.items as (TaskItem & {
        createdAt: string;
        updatedAt: string;
      })[]).map(
        ({ createdAt, updatedAt, taskMetadata, ...restTask }) => restTask
      ),
    };
  }

  private convertJobForExport(jobs: JobsWithSignature, isCsv?: boolean) {
    return jobs.items.map(
      ({
        id,
        tenant,
        landscape,
        tenantLandscape,
        createdAt,
        updatedAt,
        tasks,
        ...restJob
      }) => ({
        ...restJob,
        tasks: isCsv
          ? JSON.stringify(this.convertTasks(tasks))
          : this.convertTasks(tasks),
        signature: jobs.signature,
      })
    );
  }

  private exportJobFile() {
    const selectedJobs =
      this.exportTarget === 'all' ? this.jobs : this.checkedJobs;
    const sortedJobs = selectedJobs.sort((a, b) =>
      a.jobCode > b.jobCode ? 1 : -1
    );
    const signatureSourceText = sortedJobs
      .map(
        job =>
          `${job.jobCode}#${job.title}#${job.description}#${
            job.hasOutputFile
          }#${JSON.stringify(this.convertTasks(job.tasks))}`
      )
      .join('');
    const signature = getSHA3of(signatureSourceText);
    const jobsWithSignature = { items: sortedJobs, signature };
    switch (this.exportFormat) {
      case 'csv': {
        this.exportJobToCsvFile(jobsWithSignature);
        break;
      }
      case 'yaml': {
        this.exportJobToYamlFile(jobsWithSignature);
      }
    }
  }

  private exportJobToYamlFile(jobs: JobsWithSignature) {
    const yaml = Yaml.stringify(this.convertJobForExport(jobs));
    const blob = new Blob([yaml], { type: 'text/yaml' });
    const fileName = `jobs_${dayjs().format('YYYYMMDDHHmmss')}_${
      this.loginInfo.tenant
    }_${this.loginInfo.landscape}.yaml`;
    saveAs(blob, fileName);
  }

  private exportJobToCsvFile(jobs: JobsWithSignature) {
    const csv = Papa.unparse(this.convertJobForExport(jobs, true));
    const blob = new Blob([csv], { type: 'text/csv' });
    const fileName = `jobs_${dayjs().format('YYYYMMDDHHmmss')}_${
      this.loginInfo.tenant
    }_${this.loginInfo.landscape}.csv`;
    saveAs(blob, fileName);
  }
}
